import React from 'react';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { CtaButton } from '@tassomutuo-backoffice/ui';
import { toast } from 'react-toastify';

import {
  ManagedLeadProductPath,
  ManagedLeadsUpdateRequestFilters,
  requestManagedLeadsUpdate,
  requestManagedLeadUpdate,
} from '../../../services/api/managed-leads/managedLeads';
import { selectUser, useUserStore } from '../../../store/user';
import {
  describeManagedLeadsUpdateRequestSuccess,
  describeManagedLeadUpdateRequestFailure,
  MANAGED_LEAD_UPDATE_REQUEST_SENT_MESSAGE,
} from '../managedLeadUpdateRequestFeedback';

interface NotifyButtonProps {
  pending: boolean;
  onPress: () => void;
}

/**
 * The Mutui «Notifica» button: sent on click, no confirmation, and disabled
 * while the request is in flight so a double click cannot send it twice.
 */
const NotifyButton = ({ pending, onPress }: NotifyButtonProps) => (
  <CtaButton
    loading={pending}
    onClick={onPress}
    sx={{
      bgcolor: (th) => th.palette.primary.dark,
      width: { xs: '100%', sm: 'auto' },
      maxWidth: { sm: '140px' },
    }}
    variant="contained"
  >
    <MailOutlineIcon />
    <Typography fontWeight="bold" pl={1}>
      Notifica
    </Typography>
  </CtaButton>
);

/**
 * Whether the signed-in user may send a single Richiesta di aggiornamento. It
 * reads the real actor, not the impersonated one, exactly as Tasso Mutuo grants
 * the scope; the backend enforces it anyway.
 */
export const useCanRequestManagedLeadUpdate = () =>
  useUserStore(selectUser)?.role === 'admin';

interface ManagedLeadUpdateRequestButtonProps {
  product: ManagedLeadProductPath;
  dealId: string;
}

/** Single Richiesta di aggiornamento, for the owner of one In gestione lead. */
export const ManagedLeadUpdateRequestButton = ({
  product,
  dealId,
}: ManagedLeadUpdateRequestButtonProps) => {
  const mutation = useMutation({
    mutationFn: () => requestManagedLeadUpdate(product, dealId),
    onSuccess: () => toast.success(MANAGED_LEAD_UPDATE_REQUEST_SENT_MESSAGE),
    onError: (error) =>
      toast.error(describeManagedLeadUpdateRequestFailure(error)),
  });

  return (
    <NotifyButton
      pending={mutation.isPending}
      onPress={() => mutation.mutate()}
    />
  );
};

interface ManagedLeadsUpdateRequestButtonProps {
  product: ManagedLeadProductPath;
  filters: ManagedLeadsUpdateRequestFilters;
}

/**
 * Aggregate Richiesta di aggiornamento for the In gestione list. Only the
 * active filters travel: the server reruns the list and picks the leads.
 */
export const ManagedLeadsUpdateRequestButton = ({
  product,
  filters,
}: ManagedLeadsUpdateRequestButtonProps) => {
  const mutation = useMutation({
    mutationFn: () => requestManagedLeadsUpdate(product, filters),
    onSuccess: (result) =>
      toast.success(describeManagedLeadsUpdateRequestSuccess(result)),
    onError: (error) =>
      toast.error(describeManagedLeadUpdateRequestFailure(error)),
  });

  return (
    <NotifyButton
      pending={mutation.isPending}
      onPress={() => mutation.mutate()}
    />
  );
};
